import type {VotingSystem} from '../../shared/ballotTypes';

export const NAME_STORAGE_KEY = 'ballot:name';

export type BallotPicks = Record<string, number>;

const without = (picks: BallotPicks, optionId: string): BallotPicks => {
    const {[optionId]: _removed, ...rest} = picks;
    return rest;
};

export const tapOption = (
    system: VotingSystem,
    picks: BallotPicks,
    optionId: string,
): BallotPicks => {
    switch (system) {
        case 'fptp':
            return {[optionId]: 1};
        case 'approval':
            return optionId in picks ? without(picks, optionId) : {...picks, [optionId]: 1};
        case 'preferential': {
            if (!(optionId in picks)) return {...picks, [optionId]: Object.keys(picks).length + 1};
            const removed = picks[optionId];
            return Object.fromEntries(
                Object.entries(without(picks, optionId)).map(([id, rank]) => [
                    id,
                    rank > removed ? rank - 1 : rank,
                ]),
            );
        }
        case 'dots':
            return picks;
    }
};

export const dotsLeft = (picks: BallotPicks, allowance: number): number =>
    allowance - Object.values(picks).reduce((sum, n) => sum + n, 0);

export const changeDots = (
    picks: BallotPicks,
    optionId: string,
    delta: 1 | -1,
    allowance: number,
): BallotPicks => {
    if (delta === 1 && dotsLeft(picks, allowance) <= 0) return picks;
    const next = (picks[optionId] ?? 0) + delta;
    return next <= 0 ? without(picks, optionId) : {...picks, [optionId]: next};
};

export const canSubmit = (_system: VotingSystem, picks: BallotPicks): boolean =>
    Object.keys(picks).length > 0;

export const toSelections = (picks: BallotPicks): {option_id: string; value: number}[] =>
    Object.entries(picks).map(([option_id, value]) => ({option_id, value}));

export const headerTag = (system: VotingSystem, picks: BallotPicks, allowance: number): string => {
    switch (system) {
        case 'fptp':
            return 'Pick one';
        case 'preferential':
            return 'Tap in order';
        case 'dots': {
            const left = dotsLeft(picks, allowance);
            return `${left} ${left === 1 ? 'dot' : 'dots'} left of ${allowance}`;
        }
        case 'approval':
            return 'Tick all you like';
    }
};

export const submitLabel = (
    system: VotingSystem,
    picks: BallotPicks,
    options: {id: string; title: string}[],
): string => {
    switch (system) {
        case 'fptp':
            return `Vote for ${options.find((option) => option.id in picks)?.title ?? '...'}`;
        case 'preferential':
            return 'Submit ranking';
        case 'dots':
            return `Submit ${Object.values(picks).reduce((sum, n) => sum + n, 0)} dots`;
        case 'approval':
            return `Submit ${Object.keys(picks).length} picks`;
    }
};
